
import React, { useState, useEffect } from 'react';
import { TrendingUp, Download, BarChart as BarChartIcon, LineChart as LineChartIcon, Info } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar, ReferenceLine } from 'recharts';
import { toast } from 'react-hot-toast';

const SegmentPerformanceCharts = ({ 
  segmentId = 'urban',
  trendData = [],
  selectedMetric, 
  setSelectedMetric, 
  categoryFilter 
}) => {
  const [chartType, setChartType] = useState('line');
  const [showTooltip, setShowTooltip] = useState(false);
  
  // Create metric-specific data as needed
  const metricData = {
    volume: trendData,
    transaction: generateMetricData('transaction'),
    traffic: generateMetricData('traffic'),
    conversion: generateMetricData('conversion'),
    basket: generateMetricData('basket')
  };
  
  // Function to generate data for each metric type
  function generateMetricData(metricType) {
    const baseMonths = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    
    // Define baseline values for each segment and metric
    const baselineValues = {
      volume: { urban: 105, highway: 88, rural: 92, suburban: 84 },
      transaction: { urban: 850, highway: 780, rural: 520, suburban: 650 }, // Avg Transaction Value (THB)
      traffic: { urban: 847, highway: 925, rural: 625, suburban: 725 },
      conversion: { urban: 68, highway: 52, rural: 58, suburban: 62 },
      basket: { urban: 752, highway: 820, rural: 580, suburban: 650 }
    };
    
    // Generate realistic-looking data with some slight trends
    return baseMonths.map((month, index) => {
      // Add a small growth factor based on index
      const growthFactor = 1 + (index * 0.02);
      
      // Random variation around the baseline (±3%)
      const getVariation = () => 0.97 + Math.random() * 0.06;
      
      return {
        month,
        urban: Math.round(baselineValues[metricType].urban * growthFactor * getVariation()),
        highway: Math.round(baselineValues[metricType].highway * (1 - index * 0.01) * getVariation()),
        rural: Math.round(baselineValues[metricType].rural * (1 + index * 0.01) * getVariation()),
        suburban: Math.round(baselineValues[metricType].suburban * (1 - index * 0.005) * getVariation()),
        urbanCafe: Math.round((baselineValues[metricType].urban * 1.1) * growthFactor * getVariation()),
        highwayCafe: Math.round((baselineValues[metricType].highway * 1.05) * (1 - index * 0.01) * getVariation()),
        ruralCafe: Math.round((baselineValues[metricType].rural * 1.08) * (1 + index * 0.01) * getVariation()),
        suburbanCafe: Math.round((baselineValues[metricType].suburban * 1.12) * (1 - index * 0.005) * getVariation())
      };
    });
  }
  
  // Map for segment colors
  const segmentColors = {
    urban: '#8B5CF6',
    highway: '#3B82F6',
    rural: '#10B981',
    suburban: '#F59E0B'
  };

  // Reference values for each metric (industry benchmarks or targets)
  const referenceValues = {
    volume: 100,
    transaction: 700, // Target average transaction value (THB)
    traffic: 700,
    conversion: 60,
    basket: 650
  };
  
  // Get appropriate label for y-axis based on selected metric
  const getYAxisLabel = () => {
    switch(selectedMetric) {
      case 'volume': return 'Volume Index';
      case 'transaction': return 'Avg. Transaction Value (THB)';
      case 'traffic': return 'Daily Traffic';
      case 'conversion': return 'Conversion Rate (%)';
      case 'basket': return 'Basket Size (฿)';
      default: return '';
    }
  };

  // Formatter for tooltip values based on metric type
  const formatTooltipValue = (value, name) => {
    if (selectedMetric === 'transaction') {
      return [`${value} ฿`, name];
    } else if (selectedMetric === 'volume') {
      return [`${value}`, name];
    } else if (selectedMetric === 'conversion') {
      return [`${value}%`, name];
    } else {
      return [`${value}`, name];
    }
  };

  // Calculate segment performance trend
  const calculateTrend = (segmentKey) => {
    const data = metricData[selectedMetric];
    if (!data || data.length < 2) return { value: 0, direction: 'neutral' };
    
    const latest = data[data.length - 1][categoryFilter === 'cafe' ? `${segmentKey}Cafe` : segmentKey];
    const previous = data[data.length - 2][categoryFilter === 'cafe' ? `${segmentKey}Cafe` : segmentKey];
    
    if (latest === undefined || previous === undefined) {
      return { value: 0, direction: 'neutral' };
    }
    
    const change = ((latest - previous) / previous) * 100;
    return {
      value: Math.abs(change).toFixed(1),
      direction: change >= 0 ? 'up' : 'down'
    };
  };

  // Function to get class name for trend
  const getTrendClass = (direction) => {
    return direction === 'up' 
      ? 'bg-green-100 text-green-800' 
      : direction === 'down' 
        ? 'bg-red-100 text-red-800' 
        : 'bg-gray-100 text-gray-800';
  };

  // Handle metric selection
  const handleMetricChange = (metric) => {
    setSelectedMetric(metric);
    toast.success(`Switched to ${metric} analysis`);
  };
  
  // Generate chart title based on selected metric and category
  const getChartTitle = () => {
    const metricName = {
      'volume': 'Volume',
      'transaction': 'Avg. Transaction Value',
      'traffic': 'Traffic',
      'conversion': 'Conversion Rate',
      'basket': 'Basket Size'
    }[selectedMetric] || 'Volume';
    
    const categoryName = {
      'fuel': 'Fuel',
      'cafe': 'Café Amazon',
      'convenience': 'Convenience Store',
      'carcare': 'Car Care Services'
    }[categoryFilter] || 'Fuel';
    
    return `${metricName} Performance Trends (${categoryName})`;
  };

  // Get insight for the current metric and segment
  const getInsight = () => {
    const insights = {
      volume: {
        urban: "Urban Premium segment shows strong volume growth, outperforming all other segments due to high traffic areas and premium product selection.",
        highway: "Highway Transit segment is experiencing a declining trend in volume, suggesting increased competition from nearby alternatives.",
        rural: "Rural Standard segment shows steady growth in volume, likely due to its position as a community cornerstone with minimal competition.",
        suburban: "Suburban Mixed segment is underperforming in volume metrics, consider targeted promotions to stimulate traffic."
      },
      transaction: {
        urban: "Urban Premium maintains the highest transaction value of ฿850 due to premium product mix and less price sensitivity among urban customers.",
        highway: "Highway Transit shows consistent transaction values averaging ฿780, driven by travelers making larger purchases for journeys.",
        rural: "Rural Standard has the lowest transaction value (฿520) but shows steady growth, indicating opportunity for value-added services.",
        suburban: "Suburban Mixed transaction value averages ฿650 with weekend peaks, suggesting potential for targeted weekend promotions."
      },
      traffic: {
        urban: "Urban Premium traffic shows daily peaks during commute hours, capitalize with quick-service offerings.",
        highway: "Highway Transit has highest overall traffic but may be missing conversion opportunities. Focus on speed of service.",
        rural: "Rural Standard traffic is lower but more consistent throughout the week with strong weekend performance.",
        suburban: "Suburban Mixed shows strong evening and weekend traffic patterns, optimize staffing accordingly."
      },
      conversion: {
        urban: "Urban Premium conversion rates exceed targets, indicating effective merchandising and product placement.",
        highway: "Highway Transit conversion rates are below average despite high traffic, optimize impulse purchase displays.",
        rural: "Rural Standard conversion is trending up, leverage community connections for continued growth.",
        suburban: "Suburban Mixed conversion rates vary by day part, indicating opportunity for targeted daypart promotions."
      },
      basket: {
        urban: "Urban Premium basket size is strong with coffee and premium snacks driving incremental purchases.",
        highway: "Highway Transit shows the highest average basket size, driven by larger-quantity purchases for journeys.",
        rural: "Rural Standard basket size is lower but growing as additional services are added.",
        suburban: "Suburban Mixed basket size fluctuates seasonally, consider weather-based promotions."
      }
    };
    
    return insights[selectedMetric]?.[segmentId] || "Select a segment for detailed insights.";
  };
  
  // Initialize with transaction metric by default
  useEffect(() => {
    if (!selectedMetric) {
      setSelectedMetric('transaction');
    }
  }, [selectedMetric, setSelectedMetric]);

  return (
    <div className="bg-white rounded-lg shadow mb-6">
      <div className="border-b border-gray-200 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center text-lg font-medium text-gray-900">
            <TrendingUp size={20} className="mr-2 text-purple-600" />
            {getChartTitle()}
          </div>
          <div className="flex items-center space-x-2">
            <div className="p-1 bg-gray-100 rounded-md flex">
              <button
                className={`px-2 py-1 rounded ${chartType === 'line' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                onClick={() => setChartType('line')}
              >
                <LineChartIcon size={16} className="text-gray-700" />
              </button>
              <button
                className={`px-2 py-1 rounded ${chartType === 'bar' ? 'bg-white shadow-sm' : 'hover:bg-gray-200'}`}
                onClick={() => setChartType('bar')}
              >
                <BarChartIcon size={16} className="text-gray-700" />
              </button>
            </div>
            <button className="flex items-center text-sm text-purple-600 hover:text-purple-800 transition-colors px-3 py-1 border border-purple-200 rounded-md hover:bg-purple-50">
              <Download size={16} className="mr-1" />
              Export
            </button>
          </div>
        </div>
      </div>
      
      <div className="p-4">
        <div className="mb-4 flex flex-wrap gap-2">
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              selectedMetric === 'volume' 
                ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-400' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            onClick={() => handleMetricChange('volume')}
          >
            Volume
          </button>
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              selectedMetric === 'transaction' 
                ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-400' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            onClick={() => handleMetricChange('transaction')}
          >
            Avg. Transaction Value
          </button>
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              selectedMetric === 'traffic' 
                ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-400' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            onClick={() => handleMetricChange('traffic')}
          >
            Traffic
          </button>
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              selectedMetric === 'conversion' 
                ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-400' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            onClick={() => handleMetricChange('conversion')}
          >
            Conversion
          </button>
          <button 
            className={`px-3 py-1 text-xs font-medium rounded-full transition-colors ${
              selectedMetric === 'basket' 
                ? 'bg-purple-100 text-purple-800 ring-1 ring-purple-400' 
                : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
            }`}
            onClick={() => handleMetricChange('basket')}
          >
            Basket Size
          </button>
        </div>
        
        {/* Insight Panel */}
        <div className="mb-4 p-3 bg-blue-50 border border-blue-100 rounded-lg flex items-start">
          <Info size={18} className="text-blue-500 mt-0.5 mr-2 flex-shrink-0" />
          <div className="text-sm text-blue-800">
            <strong>Insight:</strong> {getInsight()}
          </div>
        </div>
        
        {/* Performance Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-4">
          {Object.entries(segmentColors).map(([segment, color]) => {
            const trend = calculateTrend(segment);
            const isSelected = segmentId === segment;
            
            return (
              <div key={segment} className={`p-3 rounded-lg border ${isSelected ? 'border-purple-300 bg-purple-50' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center">
                    <div className="w-3 h-3 rounded-full mr-2" style={{ backgroundColor: color }}></div>
                    <span className="text-xs font-medium text-gray-700">
                      {segment === 'urban' ? 'Urban Premium' :
                       segment === 'highway' ? 'Highway Transit' :
                       segment === 'rural' ? 'Rural Standard' : 'Suburban Mixed'}
                    </span>
                  </div>
                  <div className={`px-2 py-0.5 text-xs rounded-full ${getTrendClass(trend.direction)}`}>
                    {trend.direction === 'up' ? '+' : ''}{trend.value}%
                  </div>
                </div>
                <div className="text-lg font-semibold">
                  {selectedMetric === 'transaction' 
                    ? metricData[selectedMetric][metricData[selectedMetric].length - 1][categoryFilter === 'cafe' ? `${segment}Cafe` : segment] + ' ฿'
                    : selectedMetric === 'volume'
                      ? metricData[selectedMetric][metricData[selectedMetric].length - 1][categoryFilter === 'cafe' ? `${segment}Cafe` : segment]
                      : selectedMetric === 'conversion'
                        ? metricData[selectedMetric][metricData[selectedMetric].length - 1][categoryFilter === 'cafe' ? `${segment}Cafe` : segment] + '%'
                        : metricData[selectedMetric][metricData[selectedMetric].length - 1][categoryFilter === 'cafe' ? `${segment}Cafe` : segment]
                  }
                </div>
              </div>
            );
          })}
        </div>
        
        <div className="h-80 w-full">
          <ResponsiveContainer width="100%" height="100%">
            {chartType === 'line' ? (
              <LineChart
                data={metricData[selectedMetric]}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" />
                <YAxis label={{ value: getYAxisLabel(), angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #f0f0f0'
                  }}
                  formatter={formatTooltipValue}
                />
                <Legend />
                <ReferenceLine 
                  y={referenceValues[selectedMetric]} 
                  stroke="#94A3B8" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Target/Benchmark', 
                    position: 'right', 
                    fill: '#94A3B8', 
                    fontSize: 12 
                  }} 
                />
                
                <Line 
                  type="monotone" 
                  dataKey={categoryFilter === 'cafe' ? 'urbanCafe' : 'urban'} 
                  name="Urban Premium" 
                  stroke={segmentColors.urban} 
                  strokeWidth={segmentId === 'urban' ? 3 : 1.5} 
                  dot={segmentId === 'urban'} 
                  activeDot={segmentId === 'urban' ? { r: 6 } : { r: 4 }}
                  opacity={segmentId === 'urban' ? 1 : 0.6}
                />
                <Line 
                  type="monotone" 
                  dataKey={categoryFilter === 'cafe' ? 'highwayCafe' : 'highway'} 
                  name="Highway Transit" 
                  stroke={segmentColors.highway} 
                  strokeWidth={segmentId === 'highway' ? 3 : 1.5} 
                  dot={segmentId === 'highway'} 
                  activeDot={segmentId === 'highway' ? { r: 6 } : { r: 4 }}
                  opacity={segmentId === 'highway' ? 1 : 0.6}
                />
                <Line 
                  type="monotone" 
                  dataKey={categoryFilter === 'cafe' ? 'ruralCafe' : 'rural'} 
                  name="Rural Standard" 
                  stroke={segmentColors.rural} 
                  strokeWidth={segmentId === 'rural' ? 3 : 1.5} 
                  dot={segmentId === 'rural'} 
                  activeDot={segmentId === 'rural' ? { r: 6 } : { r: 4 }}
                  opacity={segmentId === 'rural' ? 1 : 0.6}
                />
                <Line 
                  type="monotone" 
                  dataKey={categoryFilter === 'cafe' ? 'suburbanCafe' : 'suburban'} 
                  name="Suburban Mixed" 
                  stroke={segmentColors.suburban} 
                  strokeWidth={segmentId === 'suburban' ? 3 : 1.5} 
                  dot={segmentId === 'suburban'} 
                  activeDot={segmentId === 'suburban' ? { r: 6 } : { r: 4 }}
                  opacity={segmentId === 'suburban' ? 1 : 0.6}
                />
              </LineChart>
            ) : (
              <BarChart
                data={metricData[selectedMetric]}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis dataKey="month" />
                <YAxis label={{ value: getYAxisLabel(), angle: -90, position: 'insideLeft', style: { textAnchor: 'middle' } }} />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: 'white', 
                    borderRadius: '0.5rem',
                    boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
                    border: '1px solid #f0f0f0'
                  }}
                  formatter={formatTooltipValue}
                />
                <Legend />
                <ReferenceLine 
                  y={referenceValues[selectedMetric]} 
                  stroke="#94A3B8" 
                  strokeDasharray="3 3" 
                  label={{ 
                    value: 'Target/Benchmark', 
                    position: 'right', 
                    fill: '#94A3B8', 
                    fontSize: 12 
                  }} 
                />
                
                <Bar 
                  dataKey={categoryFilter === 'cafe' ? 'urbanCafe' : 'urban'} 
                  name="Urban Premium" 
                  fill={segmentColors.urban} 
                  opacity={segmentId === 'urban' ? 1 : 0.6}
                />
                <Bar 
                  dataKey={categoryFilter === 'cafe' ? 'highwayCafe' : 'highway'} 
                  name="Highway Transit" 
                  fill={segmentColors.highway} 
                  opacity={segmentId === 'highway' ? 1 : 0.6}
                />
                <Bar 
                  dataKey={categoryFilter === 'cafe' ? 'ruralCafe' : 'rural'} 
                  name="Rural Standard" 
                  fill={segmentColors.rural} 
                  opacity={segmentId === 'rural' ? 1 : 0.6}
                />
                <Bar 
                  dataKey={categoryFilter === 'cafe' ? 'suburbanCafe' : 'suburban'} 
                  name="Suburban Mixed" 
                  fill={segmentColors.suburban} 
                  opacity={segmentId === 'suburban' ? 1 : 0.6}
                />
              </BarChart>
            )}
          </ResponsiveContainer>
        </div>
        
        <div className="mt-4 flex flex-wrap gap-4 justify-center">
          {Object.entries(segmentColors).map(([segment, color]) => (
            <div key={segment} className="flex items-center space-x-2">
              <div 
                className="w-3 h-3 rounded-full" 
                style={{ backgroundColor: color, opacity: segmentId === segment ? 1 : 0.6 }}
              ></div>
              <span className={`text-xs ${segmentId === segment ? 'font-medium' : 'text-gray-600'}`}>
                {segment === 'urban' ? 'Urban Premium' :
                 segment === 'highway' ? 'Highway Transit' :
                 segment === 'rural' ? 'Rural Standard' : 'Suburban Mixed'}
              </span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SegmentPerformanceCharts;
